import { Link } from "react-router-dom";
import { LinkedinLogo, InstagramLogo, XLogo, FacebookLogo } from "@phosphor-icons/react";
import { useLang } from "../i18n/LanguageContext";

export default function Footer() {
  const { t } = useLang();
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#B8CCE0] border-t border-[#D5DEE8]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 py-12 md:py-14">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <Link to="/" className="flex items-center gap-4 group w-fit">
              <span className="relative inline-flex items-center justify-center w-20 h-20 overflow-hidden">
                <img
                  src="/brand/omc-logo-512.png"
                  alt="OMC LTD"
                  className="w-20 h-20 object-contain"
                />
              </span>
              <div className="leading-tight">
                <div className="font-serif-display font-semibold text-3xl md:text-4xl text-[#0F1E33] group-hover:text-[#1E3A5F] transition-colors">
                  OMC LTD
                </div>
                <div className="font-mono text-[11px] font-medium tracking-[0.25em] uppercase text-[#1E3A5F] mt-1">
                  {t("common.brand")}
                </div>
              </div>
            </Link>
            <p className="mt-5 text-[#1F2D40] text-sm leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="mt-5 space-y-1.5 text-sm">
              <a href="mailto:omcltd.eu@gmail.com" className="block text-[#1E3A5F] hover:underline break-all">omcltd.eu@gmail.com</a>
              <a href="mailto:omc.ltd@1eo.uk" className="block text-[#1E3A5F] hover:underline break-all">omc.ltd@1eo.uk</a>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <a
                href="https://www.linkedin.com/company/omc-ltd-original-motor-components/"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-linkedin"
                aria-label="OMC LTD on LinkedIn"
                className="inline-flex items-center gap-2 px-3 py-2 bg-[#1E3A5F] text-[#D4AF37] hover:bg-[#15273F] transition-colors font-mono text-[10px] tracking-[0.25em] uppercase"
              >
                <LinkedinLogo size={18} weight="fill" />
                LinkedIn
              </a>
              <a
                href="https://www.instagram.com/omcltd_eu/"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                aria-label="OMC LTD on Instagram"
                className="inline-flex items-center gap-2 px-3 py-2 bg-[#1E3A5F] text-[#D4AF37] hover:bg-[#15273F] transition-colors font-mono text-[10px] tracking-[0.25em] uppercase"
              >
                <InstagramLogo size={18} weight="fill" />
                Instagram
              </a>
              <a
                href="https://x.com/OMCLTD_EU"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-x"
                aria-label="OMC LTD on X"
                className="inline-flex items-center gap-2 px-3 py-2 bg-[#1E3A5F] text-[#D4AF37] hover:bg-[#15273F] transition-colors font-mono text-[10px] tracking-[0.25em] uppercase"
              >
                <XLogo size={18} weight="fill" />
                X
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61592227406148"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook"
                aria-label="OMC LTD on Facebook"
                className="inline-flex items-center gap-2 px-3 py-2 bg-[#1E3A5F] text-[#D4AF37] hover:bg-[#15273F] transition-colors font-mono text-[10px] tracking-[0.25em] uppercase"
              >
                <FacebookLogo size={18} weight="fill" />
                Facebook
              </a>
            </div>
          </div>

          <div className="col-span-12 md:col-span-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <div className="overline-maroon mb-3">{t("footer.ukOffice")}</div>
              <ul className="space-y-1.5 text-[#1F2D40] text-sm">
                <li>Croydon Close, Coventry</li>
                <li>West Midlands · CV3 5EJ</li>
                <li className="pt-2 font-mono text-[11px] text-[#7B8AA0]">REG 12858522</li>
                <li className="font-mono text-[11px] text-[#7B8AA0]">VAT 482425289</li>
              </ul>
            </div>
            <div>
              <div className="overline-maroon mb-3">{t("footer.beOffice")}</div>
              <ul className="space-y-1.5 text-[#1F2D40] text-sm">
                <li>Helene Dutrieulaan 9/201</li>
                <li>9051 Gent · Belgium</li>
              </ul>
            </div>
            <div>
              <div className="overline-maroon mb-3">{t("footer.frOffice")}</div>
              <ul className="space-y-1.5 text-[#1F2D40] text-sm">
                <li>46 rue Henri Faisans</li>
                <li>64000 Pau · France</li>
              </ul>
            </div>
          </div>

          <div className="col-span-12 md:col-span-2">
            <div className="overline-maroon mb-3">{t("footer.sitemap")}</div>
            <ul className="space-y-1.5 text-[#1F2D40] text-sm">
              <li><Link to="/" className="hover:text-[#1E3A5F]">{t("nav.home")}</Link></li>
              <li><Link to="/about" className="hover:text-[#1E3A5F]">{t("nav.about")}</Link></li>
              <li><Link to="/range" className="hover:text-[#1E3A5F]">{t("nav.products")}</Link></li>
              <li><Link to="/quality" className="hover:text-[#1E3A5F]">{t("nav.quality")}</Link></li>
              <li><Link to="/contact" className="hover:text-[#1E3A5F]">{t("nav.contact")}</Link></li>
            </ul>
          </div>
        </div>

        <div className="hairline-gold my-8 md:my-10" />

        <div className="flex flex-wrap items-center justify-between gap-3 text-[10px] md:text-xs text-[#7B8AA0] font-mono uppercase tracking-widest">
          <span>© {new Date().getFullYear()} OMC LTD · {t("footer.rights")}</span>
          <span>{t("footer.regions")}</span>
        </div>
      </div>
    </footer>
  );
}
